import React, { useState, useEffect } from 'react'
import { TrendingUp, TrendingDown, Users, DollarSign, Star, Calendar, Clock, Target, BarChart3, PieChart, Activity, Award, Eye, Heart, MessageCircle, Video, BookOpen, Filter } from 'lucide-react'
import { GuruConnectApi } from '../../services/guruConnectApi'

interface AnalyticsData {
  overview: {
    total_revenue: number
    total_sessions: number
    total_clients: number
    average_rating: number
    revenue_growth: number
    session_growth: number
    client_growth: number
    rating_change: number
  }
  revenue: {
    monthly: { month: string; revenue: number }[]
    by_session_type: { type: string; revenue: number; sessions: number }[]
    by_client_tier: { tier: string; revenue: number; clients: number }[]
  }
  sessions: {
    monthly: { month: string; sessions: number }[]
    by_type: { type: string; count: number; revenue: number }[]
    by_duration: { duration: string; count: number; avg_rating: number }[]
    completion_rate: number
    cancellation_rate: number
  }
  clients: {
    new_clients: { month: string; count: number }[]
    retention_rate: number
    average_lifetime_value: number
    by_demographic: { demographic: string; count: number; revenue: number }[]
  }
  content: {
    total_views: number
    total_likes: number
    total_purchases: number
    by_type: { type: string; views: number; likes: number; purchases: number }[]
    top_performing: { title: string; views: number; revenue: number }[]
  }
  performance: {
    response_time: number
    availability_rate: number
    client_satisfaction: number
    repeat_client_rate: number
  }
}

interface AdvancedAnalyticsProps {
  userRole: 'guru' | 'client' | 'admin'
  userId?: string
  timeRange?: '7d' | '30d' | '90d' | '1y'
}

export const AdvancedAnalytics: React.FC<AdvancedAnalyticsProps> = ({
  userRole,
  userId,
  timeRange = '30d'
}) => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedTimeRange, setSelectedTimeRange] = useState(timeRange)
  const [selectedMetric, setSelectedMetric] = useState('revenue')

  useEffect(() => {
    loadAnalytics()
  }, [selectedTimeRange, userRole, userId])

  const loadAnalytics = async () => {
    try {
      setLoading(true)
      setError('')
      
      // In a real app, this would fetch analytics data from the backend
      const mockData: AnalyticsData = {
        overview: {
          total_revenue: 15420,
          total_sessions: 89,
          total_clients: 23,
          average_rating: 4.7,
          revenue_growth: 12.5,
          session_growth: 8.3,
          client_growth: 15.2,
          rating_change: 0.2
        },
        revenue: {
          monthly: [
            { month: 'Jan', revenue: 1200 },
            { month: 'Feb', revenue: 1350 },
            { month: 'Mar', revenue: 1420 },
            { month: 'Apr', revenue: 1580 },
            { month: 'May', revenue: 1650 },
            { month: 'Jun', revenue: 1720 }
          ],
          by_session_type: [
            { type: 'Consultation', revenue: 8200, sessions: 45 },
            { type: 'Follow-up', revenue: 4200, sessions: 28 },
            { type: 'Emergency', revenue: 3020, sessions: 16 }
          ],
          by_client_tier: [
            { tier: 'Premium', revenue: 9200, clients: 8 },
            { tier: 'Standard', revenue: 5200, clients: 12 },
            { tier: 'Basic', revenue: 1020, clients: 3 }
          ]
        },
        sessions: {
          monthly: [
            { month: 'Jan', sessions: 12 },
            { month: 'Feb', sessions: 14 },
            { month: 'Mar', sessions: 15 },
            { month: 'Apr', sessions: 16 },
            { month: 'May', sessions: 17 },
            { month: 'Jun', sessions: 18 }
          ],
          by_type: [
            { type: 'Video Call', count: 45, revenue: 8200 },
            { type: 'In-Person', count: 28, revenue: 4200 },
            { type: 'Phone Call', count: 16, revenue: 3020 }
          ],
          by_duration: [
            { duration: '30 min', count: 25, avg_rating: 4.6 },
            { duration: '60 min', count: 45, avg_rating: 4.8 },
            { duration: '90 min', count: 19, avg_rating: 4.9 }
          ],
          completion_rate: 94.2,
          cancellation_rate: 5.8
        },
        clients: {
          new_clients: [
            { month: 'Jan', count: 3 },
            { month: 'Feb', count: 4 },
            { month: 'Mar', count: 5 },
            { month: 'Apr', count: 3 },
            { month: 'May', count: 4 },
            { month: 'Jun', count: 4 }
          ],
          retention_rate: 87.5,
          average_lifetime_value: 670,
          by_demographic: [
            { demographic: '25-34', count: 8, revenue: 5200 },
            { demographic: '35-44', count: 10, revenue: 6800 },
            { demographic: '45-54', count: 5, revenue: 3400 }
          ]
        },
        content: {
          total_views: 15420,
          total_likes: 892,
          total_purchases: 156,
          by_type: [
            { type: 'Articles', views: 8200, likes: 450, purchases: 89 },
            { type: 'Videos', views: 5200, likes: 320, purchases: 45 },
            { type: 'Courses', views: 2020, likes: 122, purchases: 22 }
          ],
          top_performing: [
            { title: 'Diabetes Management Guide', views: 1200, revenue: 450 },
            { title: 'Nutrition Fundamentals', views: 980, revenue: 320 },
            { title: 'Stress Reduction Techniques', views: 850, revenue: 280 }
          ]
        },
        performance: {
          response_time: 2.3,
          availability_rate: 95.8,
          client_satisfaction: 4.7,
          repeat_client_rate: 78.3
        }
      }

      setAnalyticsData(mockData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const formatPercentage = (value: number) => {
    return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`
  }

  const getGrowthColor = (value: number) => {
    return value >= 0 ? 'text-green-600' : 'text-red-600'
  }

  const getGrowthIcon = (value: number) => {
    return value >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-300">Loading analytics...</span>
      </div>
    )
  }

  if (!analyticsData) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          No Analytics Data Available
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          Start using the platform to see your analytics and performance metrics.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Advanced Analytics</h2>
          <p className="text-gray-600 dark:text-gray-300">
            Comprehensive insights into your performance and business metrics
          </p>
        </div>
        
        <div className="flex items-center space-x-4">
          <select
            value={selectedTimeRange}
            onChange={(e) => setSelectedTimeRange(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
          
          <button
            onClick={loadAnalytics}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
          >
            <Activity className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
            <div className={`flex items-center space-x-1 ${getGrowthColor(analyticsData.overview.revenue_growth)}`}>
              {getGrowthIcon(analyticsData.overview.revenue_growth)}
              <span className="text-sm font-medium">{formatPercentage(analyticsData.overview.revenue_growth)}</span>
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
            {formatCurrency(analyticsData.overview.total_revenue)}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">Total Revenue</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
              <Video className="w-6 h-6 text-blue-600" />
            </div>
            <div className={`flex items-center space-x-1 ${getGrowthColor(analyticsData.overview.session_growth)}`}>
              {getGrowthIcon(analyticsData.overview.session_growth)}
              <span className="text-sm font-medium">{formatPercentage(analyticsData.overview.session_growth)}</span>
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
            {analyticsData.overview.total_sessions}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">Total Sessions</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
            <div className={`flex items-center space-x-1 ${getGrowthColor(analyticsData.overview.client_growth)}`}>
              {getGrowthIcon(analyticsData.overview.client_growth)}
              <span className="text-sm font-medium">{formatPercentage(analyticsData.overview.client_growth)}</span>
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
            {analyticsData.overview.total_clients}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">Total Clients</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900 rounded-lg flex items-center justify-center">
              <Star className="w-6 h-6 text-yellow-600" />
            </div>
            <div className={`flex items-center space-x-1 ${getGrowthColor(analyticsData.overview.rating_change)}`}>
              {getGrowthIcon(analyticsData.overview.rating_change)}
              <span className="text-sm font-medium">{formatPercentage(analyticsData.overview.rating_change)}</span>
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
            {analyticsData.overview.average_rating.toFixed(1)}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">Average Rating</p>
        </div>
      </div>

      {/* Detailed Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Analysis */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Revenue Analysis</h3>
            <DollarSign className="w-5 h-5 text-green-600" />
          </div>
          
          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-2">Revenue by Session Type</h4>
              <div className="space-y-2">
                {analyticsData.revenue.by_session_type.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{item.type}</span>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm font-medium">{item.sessions} sessions</span>
                      <span className="text-sm font-bold text-green-600">{formatCurrency(item.revenue)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-2">Revenue by Client Tier</h4>
              <div className="space-y-2">
                {analyticsData.revenue.by_client_tier.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{item.tier}</span>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm font-medium">{item.clients} clients</span>
                      <span className="text-sm font-bold text-green-600">{formatCurrency(item.revenue)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Session Performance */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Session Performance</h3>
            <Video className="w-5 h-5 text-blue-600" />
          </div>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{analyticsData.sessions.completion_rate}%</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Completion Rate</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{analyticsData.sessions.cancellation_rate}%</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Cancellation Rate</div>
              </div>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-2">Sessions by Duration</h4>
              <div className="space-y-2">
                {analyticsData.sessions.by_duration.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{item.duration}</span>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm font-medium">{item.count} sessions</span>
                      <div className="flex items-center space-x-1">
                        <Star className="w-3 h-3 text-yellow-400 fill-current" />
                        <span className="text-sm">{item.avg_rating}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Client Insights */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Client Insights</h3>
            <Users className="w-5 h-5 text-purple-600" />
          </div>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{analyticsData.clients.retention_rate}%</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Retention Rate</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{formatCurrency(analyticsData.clients.average_lifetime_value)}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Avg. Lifetime Value</div>
              </div>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-2">Clients by Age Group</h4>
              <div className="space-y-2">
                {analyticsData.clients.by_demographic.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{item.demographic}</span>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm font-medium">{item.count} clients</span>
                      <span className="text-sm font-bold text-green-600">{formatCurrency(item.revenue)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Content Performance */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Content Performance</h3>
            <BookOpen className="w-5 h-5 text-indigo-600" />
          </div>
          
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{analyticsData.content.total_views.toLocaleString()}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Total Views</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{analyticsData.content.total_likes.toLocaleString()}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Total Likes</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{analyticsData.content.total_purchases}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Purchases</div>
              </div>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-2">Top Performing Content</h4>
              <div className="space-y-2">
                {analyticsData.content.top_performing.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400 truncate max-w-32">{item.title}</span>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm font-medium">{item.views} views</span>
                      <span className="text-sm font-bold text-green-600">{formatCurrency(item.revenue)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Performance Metrics</h3>
          <Award className="w-5 h-5 text-yellow-600" />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-3">
              <Clock className="w-8 h-8 text-blue-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {analyticsData.performance.response_time}h
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Avg. Response Time</div>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-3">
              <Calendar className="w-8 h-8 text-green-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {analyticsData.performance.availability_rate}%
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Availability Rate</div>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900 rounded-full flex items-center justify-center mx-auto mb-3">
              <Star className="w-8 h-8 text-yellow-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {analyticsData.performance.client_satisfaction}/5
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Client Satisfaction</div>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center mx-auto mb-3">
              <Users className="w-8 h-8 text-purple-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {analyticsData.performance.repeat_client_rate}%
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Repeat Client Rate</div>
          </div>
        </div>
      </div>
    </div>
  )
} 